import axios from "axios";
import { apiErrorMessage } from "@/utils/apiError-helper";

/**
 * A refusal from the entitlement layer.
 *
 * The server answers a plan violation with 402 — not 403 — because the request is well formed and
 * the caller is authorized; the ORGANIZATION's plan does not cover it. That distinction is what
 * decides whether the UI shows an upsell or an access error.
 *
 * A 402 only appears when a gate and the server disagree, which is exactly when a generic toast
 * is least useful: the client saw an unlocked control, used it, and needs to know which limit
 * they met.
 */
export interface EntitlementRefusal {
  resource: string;
  constraint: string;
  attempted: unknown;
  allowed: unknown;
  message: string;
}

/**
 * A sentence for a client, not for an engineer.
 *
 * The server's own message names the resource and constraint as the code spells them, which is
 * right for a log and wrong for a screen: "Good Starter does not include wallet.multi_group" was
 * shown to a client trying to add a second group.
 *
 * Keyed on `resource.constraint`, not on resource alone. One noun per resource cannot work —
 * `campaign_send` covers recipients, regions AND send dates, and a client who tried one of them
 * should not be told about the other two.
 *
 * The plan is never NAMED here. "Good Starter does not include X" reads as an accusation and goes
 * stale the moment plans are renamed; "Your plan" is both kinder and durable.
 */
interface RefusalCopy {
  /** The thing they tried to do, as a noun phrase. */
  thing: string;
  /** A quantity constraint says how many are permitted; a set or flag simply does not include it. */
  quantity?: boolean;
}

const REFUSAL_COPY: Record<string, RefusalCopy> = {
  "campaign_send.max_recipients": { thing: "recipients in one campaign", quantity: true },
  "campaign_send.regions": { thing: "sending to that country" },
  "campaign_send.scheduling": { thing: "that send schedule" },
  "campaign_budget.funding": { thing: "that way of paying for a campaign" },
  "campaign_reminders.max": { thing: "gift reminders", quantity: true },
  "collection.edit": { thing: "editing gift collections" },
  "landing.customize": { thing: "that landing page customization" },
  "unboxing.customize": { thing: "that unboxing customization" },
  "users.max": { thing: "users", quantity: true },
  "wallet.multi_group": { thing: "more than one group" },
  "redemption.links": { thing: "that redemption link type" },
  "merch.access": { thing: "branded merch" },
  "contacts.automation": { thing: "automated gift sending" },
  "reporting.features": { thing: "that report" },
  "billing.terms": { thing: "that billing arrangement" },
};

export function refusalMessage(r: EntitlementRefusal): string {
  const copy = REFUSAL_COPY[`${r.resource}.${r.constraint}`];

  // An unmapped constraint is a new entitlement nobody wrote copy for. Say the honest general
  // thing rather than fall back to the technical string, which is what this exists to prevent.
  if (!copy) return "Your plan doesn't include this. Upgrade to unlock it.";

  // A quantity of zero is not a limit, it is an absence — "your plan allows 0 gift reminders"
  // tells someone their allowance rather than that the feature is not theirs.
  if (copy.quantity && typeof r.allowed === "number" && r.allowed > 0) {
    return `Your plan includes up to ${r.allowed} ${copy.thing}. Upgrade to raise the limit.`;
  }
  return `Your plan doesn't include ${copy.thing}. Upgrade to unlock it.`;
}

export function asEntitlementRefusal(err: unknown): EntitlementRefusal | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 402) return null;
  const body = err.response.data as Partial<EntitlementRefusal> & { error?: string };
  return {
    resource: body.resource ?? "",
    constraint: body.constraint ?? "",
    attempted: body.attempted,
    allowed: body.allowed,
    message: body.error ?? body.message ?? "Your plan does not include this.",
  };
}

/**
 * The sentence to show for ANY failed request, entitlement refusal or not.
 *
 * One function because the choice is always the same and getting it wrong is always the same
 * mistake: a 402 carries structured fields meant to be rendered as copy, and printing its `error`
 * string instead puts an internal constraint key in front of a customer — "Good Starter does not
 * include wallet.multi_group". Anything else falls through to the server's own sentence, which for
 * a normal refusal is written for a human already.
 */
export function humanErrorMessage(err: unknown, fallback = "Something went wrong."): string {
  const refusal = asEntitlementRefusal(err);
  return refusal ? refusalMessage(refusal) : apiErrorMessage(err, fallback);
}
